'use client';

import { useState } from 'react';
import { Menu, X, ChevronDown, Plus, Minus } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';



export function ResponsiveHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileProjectsOpen, setMobileProjectsOpen] = useState(false);
  const pathname = usePathname();
  const currentPage = pathname === '/' ? 'home' : pathname.replace('/', '').replace(/\/$/, '');

  const projectSubTabs = [
    { name: 'Website', path: '/projects/website' },
    { name: 'Mobile Apps', path: '/projects/mobile-apps' },
    { name: 'AI Agents', path: '/projects/ai-agents' },
    { name: 'Full Stack', path: '/projects/full-stack' },
  ];
  const navItems = [
    { name: 'Home', page: 'home' as const, path: '/' },
    { name: 'About', page: 'about' as const, path: '/about/' },
    { name: 'Projects', page: 'projects' as const, path: '/projects/' },
    { name: 'Request Us', page: 'contact' as const, path: '/contact/' },
  ];

  const handleNavClick = () => {
    // Scroll handling is mostly automatic in Next.js, but we can enforce top scroll
    // only if already on the page, or checking scroll behavior.
    setMobileMenuOpen(false);
  };

  return (
    <header
      className="absolute top-0 left-0 right-0 w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6"
      style={{ zIndex: 999 }}
    >
      <div className="w-full">
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center justify-center gap-6 xl:gap-12 bg-black backdrop-blur-md border-2 border-purple-600 rounded-full px-[32px] py-[16px] m-[0px] max-w-7xl mx-auto">
          <Link
            href="/"
            onClick={() => handleNavClick()}
            className="min-w-[110px] px-6 py-3 rounded-full bg-black hover:bg-gradient-to-r hover:from-purple-900 hover:to-pink-600 text-white transition-all duration-300 hover:scale-105 text-center"
          >
            Home
          </Link>
          <Link
            href="/about/"
            onClick={() => handleNavClick()}
            className="min-w-[110px] px-6 py-3 rounded-full bg-black hover:bg-gradient-to-r hover:from-purple-900 hover:to-pink-600 text-white transition-all duration-300 hover:scale-105 text-center"
          >
            About
          </Link>

          {/* Logo */}
          <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 mx-2">
            <div className="absolute inset-0 overflow-hidden">
              <img
                alt="Zumify Home"
                className="absolute left-[-24.32%] max-w-none size-[147.3%] top-[-22.97%]"
                srcSet="/images/zumify-logo-150w.webp 150w, /images/zumify-logo-300w.webp 300w, /images/zumify-logo.webp 500w"
                sizes="(max-width: 640px) 64px, 80px"
                src="/images/zumify-logo.webp"
              />
            </div>
          </div>

          <div className="relative group min-w-[110px] z-50">
            <Link
              href="/projects/"
              onClick={() => handleNavClick()}
              className="flex items-center justify-center gap-1 w-full px-6 py-3 rounded-full bg-black group-hover:bg-gradient-to-r group-hover:from-purple-900 group-hover:to-pink-600 text-white transition-all duration-300 group-hover:scale-105 text-center"
            >
              Projects
              <ChevronDown className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
            </Link>

            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
              <div className="flex flex-col min-w-[200px] bg-black/95 backdrop-blur-md border-2 border-purple-600 rounded-2xl overflow-hidden shadow-xl shadow-purple-900/20">
                {projectSubTabs.map((tab) => (
                  <Link
                    key={tab.name}
                    href={tab.path}
                    onClick={() => handleNavClick()}
                    className="px-6 py-3 text-white hover:bg-purple-900/50 hover:text-purple-300 transition-colors border-b border-purple-900/50 last:border-0"
                  >
                    {tab.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <Link
            href="/contact/"
            onClick={() => handleNavClick()}
            className="min-w-[110px] px-6 py-3 rounded-full bg-black hover:bg-gradient-to-r hover:from-purple-900 hover:to-pink-600 text-white transition-all duration-300 hover:scale-105 text-center"
          >
            Request Us
          </Link>
        </nav>

        {/* Mobile Navigation */}
        <div className="lg:hidden max-w-7xl mx-auto">
          {/* Logo - Standalone in top-left (not fixed) */}
          <div className="relative w-12 h-12 flex-shrink-0 flex items-center justify-center">
            <img
              alt="Zumify Home"
              className="w-full h-full object-contain scale-[1.7]"
              srcSet="/images/zumify-logo-150w.webp 150w, /images/zumify-logo-300w.webp 300w, /images/zumify-logo.webp 500w"
              sizes="48px"
              src="/images/zumify-logo.webp"
            />
          </div>
        </div>
        {/* Black pill with only hamburger menu - now fixed on right */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="fixed right-0 sm:right-6 top-0 sm:top-6 mx-2 mt-4 bg-black/80 backdrop-blur-md border-2 border-purple-600 rounded-full px-4 py-3 text-white hover:bg-purple-900 transition-colors flex items-center justify-center lg:hidden"
          style={{ zIndex: 1000 }}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        {/* Mobile Menu Dropdown */}
        <div
          className={`fixed top-0 right-0 h-full w-64 sm:w-80 bg-black/95 border-l-2 border-purple-600 transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          style={{ zIndex: 1000 }}
        >
          <div className="flex flex-col h-full px-6">
            <div className="h-6 shrink-0" aria-hidden="true" />
            {navItems.map((item) => {
              if (item.page === 'projects') {
                return (
                  <div key={item.page} className="relative w-full border-b border-purple-900 last:border-b-0 overflow-hidden flex flex-col group">
                    <div className="flex items-center justify-between w-full relative">
                      {/* Animated background bar that extends from the left purple border */}
                      <div
                        className={`absolute left-0 top-0 h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-300 ${currentPage === item.page ? 'w-1' : 'w-0 group-hover:w-1'
                          }`}
                      />
                      {/* Background fill that extends from the bar */}
                      <div
                        className={`absolute left-0 top-0 h-full bg-gradient-to-r from-purple-900/50 to-transparent transition-all duration-300 ${currentPage === item.page ? 'w-full' : 'w-0 group-hover:w-full'
                          }`}
                      />

                      <Link
                        href={item.path}
                        onClick={() => handleNavClick()}
                        className={`relative flex-1 px-6 py-4 text-left text-white transition-all duration-300 ${currentPage === item.page ? 'text-purple-300' : ''}`}
                      >
                        <span className="relative z-10">{item.name}</span>
                      </Link>

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setMobileProjectsOpen(!mobileProjectsOpen);
                        }}
                        className="relative z-10 px-6 py-4 text-white hover:text-purple-300 transition-colors"
                        aria-label="Toggle projects submenu"
                      >
                        {mobileProjectsOpen ? <Minus className="w-5 h-5 text-purple-300" /> : <Plus className="w-5 h-5" />}
                      </button>
                    </div>

                    <div className={`transition-all duration-300 ease-in-out overflow-hidden bg-black/50 ${mobileProjectsOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div className="flex flex-col py-2">
                        {projectSubTabs.map((tab) => (
                          <Link
                            key={tab.name}
                            href={tab.path}
                            onClick={() => handleNavClick()}
                            className="pl-12 pr-6 py-3 text-sm text-gray-300 hover:text-white hover:bg-purple-900/40 transition-colors"
                          >
                            {tab.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.page}
                  href={item.path}
                  onClick={() => handleNavClick()}
                  className={`relative w-full px-6 py-4 text-left text-white transition-all duration-300 border-b border-purple-900 last:border-b-0 overflow-hidden group ${currentPage === item.page ? 'text-purple-300' : ''
                    }`}
                >
                  {/* Animated background bar that extends from the left purple border */}
                  <div
                    className={`absolute left-0 top-0 h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-300 ${currentPage === item.page ? 'w-1' : 'w-0 group-hover:w-1'
                      }`}
                  />

                  {/* Background fill that extends from the bar */}
                  <div
                    className={`absolute left-0 top-0 h-full bg-gradient-to-r from-purple-900/50 to-transparent transition-all duration-300 ${currentPage === item.page ? 'w-full' : 'w-0 group-hover:w-full'
                      }`}
                  />

                  <span className="relative z-10">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50"
            style={{ zIndex: 990 }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </div>
    </header>
  );
}

